import { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Button, IconButton, Alert, Switch,
  FormControlLabel, Divider, alpha, useTheme, LinearProgress, Paper, TextField, InputAdornment,
} from '@mui/material';
import { Close, Add, Edit, Search } from '@mui/icons-material';
import type { DocumentConfigItem, DocumentGroup } from '../../services/partnerOnboardingService';
import type { OperationalZoneRow } from '../../services/zoneService';
import {
  entityLabel, groupHubsLabel, groupIdFromRef, groupScopeSummary,
} from '../../utils/documentReviewUtils';
import { DOCUMENT_REVIEW_NOTE_SX, DOCUMENT_REVIEW_ALERT_SX, isLockedPartnerProfileDocument, isLockedPartnerProfileGroup } from '../../constants/documentReviewFlow';
import GroupZoneChips from './GroupZoneChips';

interface Props {
  open: boolean;
  group: DocumentGroup | null;
  zones?: OperationalZoneRow[];
  configs: DocumentConfigItem[];
  inactiveOnly?: boolean;
  loading?: boolean;
  onClose: () => void;
  onAddDoc: (group: DocumentGroup) => void;
  onEditDoc: (doc: DocumentConfigItem) => void;
  onToggleDoc: (id: string, isActive: boolean) => void;
  onToggleGroup: (id: string, isActive: boolean) => void;
}

export default function GroupDocumentsDrawer({
  open, group, zones = [], configs, inactiveOnly = false, loading, onClose,
  onAddDoc, onEditDoc, onToggleDoc, onToggleGroup,
}: Props) {
  const theme = useTheme();
  const [docSearch, setDocSearch] = useState('');

  useEffect(() => {
    if (!open) setDocSearch('');
  }, [open, group?._id]);

  const groupDocs = useMemo(() => {
    if (!group) return [];
    return configs
      .filter((d) => groupIdFromRef(d.groupId) === group._id)
      .filter((d) => (inactiveOnly ? d.isActive === false : true));
  }, [configs, group, inactiveOnly]);

  const docs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    return groupDocs
      .filter((d) => {
        if (!q) return true;
        const blob = `${d.name} ${d.code}`.toLowerCase();
        return blob.includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groupDocs, docSearch]);

  const activeCount = groupDocs.filter((d) => d.isActive !== false).length;
  const lockedGroup = !!(group && isLockedPartnerProfileGroup(group));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 520 } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.04) }}
        >
          <Box flex={1} minWidth={0} pr={1}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              Manage documents
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ wordBreak: 'break-word' }}>
              {group?.name || '—'}
            </Typography>
            {group && (
              <>
                <Typography variant="body2" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, mt: 0.75 }}>
                  {groupScopeSummary(group, zones)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }} alignItems="center">
                  <Chip size="small" label={entityLabel(group.entityType)} sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                  <GroupZoneChips group={group} zones={zones} chipProps={{ sx: { height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } } }} />
                  <Chip size="small" variant="outlined" label={groupHubsLabel(group)} sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                </Stack>
              </>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>

        {group && (
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Box>
                  <Typography variant="caption" fontWeight={700} display="block" sx={DOCUMENT_REVIEW_NOTE_SX}>
                    Group status
                  </Typography>
                  <Typography variant="body2" sx={DOCUMENT_REVIEW_NOTE_SX}>
                    {lockedGroup
                      ? 'Default for every partner and service. Always required.'
                      : group.isActive ? 'Partners can upload documents in this group' : 'Group is hidden from partner upload'}
                  </Typography>
                </Box>
                {lockedGroup ? (
                  <Chip size="small" color="info" label="Always on" />
                ) : (
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={group.isActive}
                        onChange={(e) => onToggleGroup(group._id, e.target.checked)}
                      />
                    )}
                    label={(
                      <Typography variant="body2" fontWeight={700}>
                        {group.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    )}
                    sx={{ m: 0 }}
                  />
                )}
              </Stack>
            </Paper>
          </Box>
        )}

        <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            label="Search documents"
            placeholder="Name or code…"
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" sx={DOCUMENT_REVIEW_NOTE_SX}>
              {activeCount} active · {docs.length} shown
            </Typography>
            {!lockedGroup && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              disabled={!group?.isActive}
              onClick={() => group && onAddDoc(group)}
            >
              Add document
            </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loading && <LinearProgress sx={{ mb: 2, borderRadius: 99 }} />}

          {!group?.isActive && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, ...DOCUMENT_REVIEW_ALERT_SX }}>
              This group is inactive. Turn the group status on before adding documents.
            </Alert>
          )}

          {group?.isActive && docs.length > 0 && activeCount === 0 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2, ...DOCUMENT_REVIEW_ALERT_SX }}>
              All documents are inactive. Use the status toggles below to activate them.
            </Alert>
          )}

          {!docs.length && !loading && (
            <Alert severity="info" sx={{ borderRadius: 2, ...DOCUMENT_REVIEW_ALERT_SX }}>
              {docSearch.trim()
                ? `No documents match "${docSearch.trim()}".`
                : inactiveOnly
                  ? 'No inactive documents in this group.'
                  : 'No documents in this group yet. Add document types partners must upload.'}
            </Alert>
          )}

          {docs.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ px: 0.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" flex={1}>
                Document
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 120, textAlign: 'right' }}>
                Status
              </Typography>
            </Stack>
          )}

          <Stack spacing={1.25} sx={{ mt: docs.length ? 1.25 : 0 }}>
            {docs.map((doc) => {
              const lockedDoc = isLockedPartnerProfileDocument(doc);
              return (
              <Box
                key={doc._id}
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  border: 1,
                  borderColor: doc.isActive === false ? alpha(theme.palette.error.main, 0.25) : 'divider',
                  bgcolor: doc.isActive === false ? alpha(theme.palette.action.hover, 0.04) : 'background.paper',
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                  <Box flex={1} minWidth={0}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" fontWeight={700}>{doc.name}</Typography>
                      {lockedDoc && <Chip size="small" color="info" variant="outlined" label="Default" />}
                    </Stack>
                    <Typography variant="caption" sx={{ ...DOCUMENT_REVIEW_NOTE_SX, display: 'block' }}>
                      {doc.code}
                      {doc.isMandatory ? ' · Required' : ' · Optional'}
                      {doc.identifierRequired ? ' · ID#' : ''}
                      {doc.expiryRequired ? ' · Expiry' : ''}
                    </Typography>
                  </Box>
                  {lockedDoc ? (
                    <Chip size="small" color="info" label="Always on" />
                  ) : (
                  <FormControlLabel
                    control={(
                      <Switch
                        size="small"
                        checked={doc.isActive !== false}
                        onChange={(e) => onToggleDoc(doc._id, e.target.checked)}
                      />
                    )}
                    label={(
                      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 52 }}>
                        {doc.isActive === false ? 'Inactive' : 'Active'}
                      </Typography>
                    )}
                    labelPlacement="start"
                    sx={{ m: 0, flexShrink: 0 }}
                  />
                  )}
                </Stack>
                {!lockedDoc && (
                  <>
                    <Divider sx={{ my: 1.25 }} />
                    <Button size="small" startIcon={<Edit />} onClick={() => onEditDoc(doc)}>
                      Edit document
                    </Button>
                  </>
                )}
              </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
